import { useLoaderStore } from '@/stores/utils/loader.store';
import { processCustomError } from '@/common/api/error-handler';
import { ErrorResponse, RequestConfig } from '@/types/api/response.types';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { attemptTokenRefresh } from '@/common/api/refresh';
import { withBearerToken, withoutCredentials } from '@/common/api/credentials';
import router from '@/router';
import { useAuthStore } from '@/stores/auth.store';
import { useNetworkStore } from '@/stores/network.store';
import { retryHandler } from '@/common/api/handlers/retryHandler';
import { useOrgStore } from '@/stores/organisations.store';
import { useVersionConflictStore } from '@/stores/utils/version-conflict.store';
import { useToastStore } from '@/stores/utils/toast.store';
import { axiosRequest } from '@/common/api/instance';
import i18n from '@/plugins/vue-i18n';
import * as Sentry from '@sentry/vue';

const reportsUrlLoaderMap: { [key: string]: string } = {
  '/risks': 'risks',
  '/orders': 'orders',
  '/cash-flow': 'revenue',
  '/variations': 'variations',
  'reports/budgets': 'expVsAvail',
  '/sites/statuses': 'siteStatusOverview',
  '/payment-requests': 'paymentRequests',
  '/accounts/overview': 'accountsOverview',
  'projects/budgets/overview': 'budgetOverview',
};

/**
 * The name a request's loader runs under, so whatever ends it ends the one it started: a report's
 * is fixed by its URL (`reportsUrlLoaderMap`), any other request's is its `requestId`. Either can be
 * undefined (an unmapped report, a request with no id), which is the anonymous loader `start` pushes.
 */
const loaderNameFor = (config: AxiosRequestConfig): string => {
  const url = config.url ?? '';

  return url.includes('reports')
    ? (Object.entries(reportsUrlLoaderMap).find(([key]) => url.includes(key))?.[1] as string)
    : (config as RequestConfig).requestId;
};

export const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  if (!(config as RequestConfig).skipGlobalLoader) {
    useLoaderStore().start(loaderNameFor(config));
  }

  return config;
};

export const responseInterceptor = (res: AxiosResponse): AxiosResponse => {
  const networkStore = useNetworkStore();

  if (networkStore.showModal) {
    networkStore.showRetryModal(false);
    networkStore.showRefreshButton(false);
  }

  if ((res.config as RequestConfig).skipGlobalLoader) return res;

  setTimeout(() => {
    useLoaderStore().end(loaderNameFor(res.config));
  }, 500);

  return res;
};

let isRefreshInProgress = false;
let pendingRequests: Array<(token: string) => void> = [];

// PE-482: stale-permission recovery. A backend 403 can mean the user's frontend
// abilities are stale (a role changed after the page loaded). Rather than let the
// action silently no-op, rehydrate abilities once in the background and retry the
// request once; a second 403 surfaces a graceful message. Single-flight so a wave
// of concurrent 403s shares one rehydration.
let rehydratePromise: Promise<void> | null = null;

const surfacePermissionToast = (): void => {
  useToastStore().showToast(
    'error',
    i18n.global.t('error.toast.permissionsChanged.title'),
    i18n.global.t('error.toast.permissionsChanged.description'),
  );
};

// Rehydration issues these bootstrap calls through this same interceptor. A 403
// on one of them must NOT re-enter recovery — it would await the very
// rehydration promise that is awaiting it (deadlock). Re-throw immediately.
const isRehydrationBootstrapRequest = (config?: RequestConfig): boolean => {
  const url = config?.url ?? '';
  const method = (config?.method ?? 'get').toLowerCase();
  return (
    url.includes('/roles/permissions') ||
    url.includes('/users/current') ||
    (method === 'get' && /\/organisations\/?(\?|$)/.test(url))
  );
};

// Refresh roles (→ the CASL ability via its watcher) plus the org membership list.
const rehydratePermissions = async (): Promise<void> => {
  await useAuthStore().getUserPermissions();
  await useOrgStore().getCurrentUserOrgs();
};

// Guards the once-per-attempt terminal outcomes (toast / no-user-orgs redirect)
// so a wave of concurrent 403s sharing one rehydration fires each at most once.
// Reset whenever a fresh rehydration attempt starts.
let isTerminalHandled = false;

// Upper bound on the rehydration round-trip. The app configures no request
// timeout, and because rehydratePromise is single-flight, a rehydration that
// never settles would wedge every current AND future 403 recovery for the
// session. Racing a timeout turns that into a graceful reject that clears the
// promise, so recovery can be attempted again.
const REHYDRATE_TIMEOUT_MS = 10_000;

// Shared single-flight: the first 403 rehydrates; concurrent 403s await the same
// promise. Cleared once settled so a later stale-permission event can rehydrate
// again.
const rehydrateOnce = (): Promise<void> => {
  if (!rehydratePromise) {
    isTerminalHandled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    rehydratePromise = Promise.race([
      rehydratePermissions(),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
          () => reject(new Error('Permission rehydration timed out')),
          REHYDRATE_TIMEOUT_MS,
        );
      }),
    ]).finally(() => {
      clearTimeout(timer);
      rehydratePromise = null;
    });
  }
  return rehydratePromise;
};

const handleStalePermission403 = async (
  error: AxiosError,
  isSilent: boolean,
): Promise<AxiosResponse> => {
  const config = error.config as RequestConfig | undefined;

  // Rehydration's own bootstrap requests bypass recovery (see above).
  if (!config || isRehydrationBootstrapRequest(config)) {
    throw error;
  }

  // Already retried once for a permission error → terminal. Covers a genuine
  // denial and a domain segregation-of-duties 403 (neither clears on
  // rehydration). Belt-and-braces: the leader also handles its repeat failure
  // inline (below); a retried request that somehow re-enters stops here.
  if (config.isPermissionRetry) {
    if (!isSilent) surfacePermissionToast();
    throw error;
  }

  try {
    await rehydrateOnce();
  } catch {
    // Rehydration itself failed — surface a message and stop; never loop. Only
    // the first caller of a shared attempt toasts (concurrent 403s dedupe). A
    // silent 403 must not claim that toast, or a concurrent 403 that is not
    // silent would go without one.
    if (!isSilent && !isTerminalHandled) {
      isTerminalHandled = true;
      surfacePermissionToast();
    }
    throw error;
  }

  // Removed from every org → terminal redirect, no retry (the request is moot).
  // Only the first caller of a shared attempt redirects (concurrent 403s dedupe).
  if (!useAuthStore().loggedInUser?.organisations?.length) {
    if (!isTerminalHandled) {
      isTerminalHandled = true;
      await router.push({ name: 'no-user-orgs' });
    }
    throw error;
  }

  // Retry the original request once, through the app's intercepted instance so
  // it mirrors the original request (loader + interceptors). RETURN its promise
  // so the caller's await receives the recovered response (no setTimeout — cf.
  // the 401 path, whose retry promise is discarded). A repeat 403 re-enters
  // onRejected and hits the isPermissionRetry terminal branch above — one toast,
  // no loop. (The interceptors↔instance import cycle is safe: axiosRequest is
  // only referenced here at call time, never at module load.)
  config.isPermissionRetry = true;
  return axiosRequest().request(config);
};

/**
 * Whether a rejection is an **aborted** request rather than a failed one.
 *
 * 🔴 **An abort is a normal outcome, not a failure**, and nothing below this line knew that.
 * `processCustomError` selects copy by status and URL and then **always** toasts, and
 * `retryHandler` would re-issue a request the caller has already replaced — so the first consumer
 * to cancel an in-flight call (the bulk-order preview, PE-682) would have toasted an error and
 * re-fetched on every cancellation.
 *
 * Checked for **first**, ahead of the retry handler, because an aborted request has no response to
 * classify and retrying one is by definition wrong. `axios.isCancel` covers both the `AbortSignal`
 * path and the legacy `CancelToken` one; `ERR_CANCELED` is the code axios ≥1.x sets alongside it,
 * kept as a belt because the two have diverged across versions.
 */
export const isAbortedRequest = (error: AxiosError): boolean =>
  axios.isCancel(error) || error.code === 'ERR_CANCELED';

/**
 * Whether the request names this response's status in one of its `RequestConfig` lists. A 401
 * never counts: its token refresh keeps the session alive for every request, not just this one.
 */
const isStatusListedIn = (error: AxiosError, list: 'handlesErrors' | 'silentErrors'): boolean => {
  const status = error.response?.status;

  return (
    status !== undefined &&
    status !== 401 &&
    ((error.config as RequestConfig | undefined)?.[list]?.includes(status) ?? false)
  );
};

// `processCustomError` would leave the description blank, since a response came back.
const surfaceTryAgainToast = (): void => {
  useToastStore().showToast(
    'error',
    i18n.global.t('error.toast.validation.catchAll.title'),
    i18n.global.t('error.toast.validation.catchAll.description'),
  );
};

const isFileDownload = (config: AxiosRequestConfig): boolean =>
  config.responseType === 'blob' || config.responseType === 'arraybuffer';

// A file download's body is always a `Blob` or an `ArrayBuffer`, truthy even when the server sent
// nothing.
const hasBody = (data: unknown): boolean => {
  if (data instanceof Blob) return data.size > 0;
  if (data instanceof ArrayBuffer) return data.byteLength > 0;

  return Boolean(data);
};

const handleRejection = async (error: AxiosError): Promise<AxiosResponse> => {
  if (isAbortedRequest(error)) {
    // The loader is still released, and that ordering is deliberate: a caller that started one has
    // no other path to end it, so returning above this would trade a spurious toast for a spinner
    // that never stops. Everything below — the retry, the 401 refresh arm, the toast — is skipped,
    // which is the whole point. No toast, no error state, no stuck loader, no retry.
    //
    // 🔴 **`end(requestId)`, never `endAll()`** (PR #2261 review). An abort is usually one request
    // among several in flight, so `endAll()` would clear loaders belonging to the others. Worse,
    // an abort races its own replacement: the replacement's request interceptor calls `start()`
    // synchronously while the abort rejection arrives a microtask later, so `endAll()` could end
    // the spinner of the request that superseded it — the mirror of the bug `a8253c38` fixed.
    // No id means the request interceptor never started a loader for it, so there is none to end.
    const { requestId } = (error.config ?? {}) as Partial<RequestConfig>;
    if (requestId) useLoaderStore().end(requestId);

    throw error;
  }

  const orgStore = useOrgStore();
  const authStore = useAuthStore();
  const config = error.config as RequestConfig | undefined;
  const skipsErrorFeedback = config?.skipErrorFeedback === true;
  const isSilent = isStatusListedIn(error, 'silentErrors');
  const isCallerHandled = isSilent || isStatusListedIn(error, 'handlesErrors');

  // Background requests and caller-handled refusals must not end unrelated requests' loaders.
  if (skipsErrorFeedback || isCallerHandled) {
    if (!config?.skipGlobalLoader) useLoaderStore().end(loaderNameFor(config ?? {}));
  } else {
    useLoaderStore().endAll();
  }

  if (!skipsErrorFeedback) {
    // Retry logic for network errors
    await retryHandler(error);
  }

  if (error.response) {
    if (error.response.status === 401) {
      if (hasBody(error.response.data)) {
        if (!skipsErrorFeedback) processCustomError(error as AxiosError<ErrorResponse>);
      } else {
        if (error.config && !(error.config as RequestConfig).isRetryRequest) {
          (error.config as RequestConfig).isRetryRequest = true;

          if (isRefreshInProgress) {
            // If a refresh is in progress, queue the request
            return new Promise((resolve, _reject) => {
              pendingRequests.push(() => {
                // Retry the original request with the new token
                if (!error.config) {
                  return;
                }

                resolve(axios.request(withBearerToken(error.config, authStore.accessToken!)));
              });
            });
          }

          // If no refresh is in progress, attempt to refresh the token
          try {
            isRefreshInProgress = true; // Lock the refresh process
            await attemptTokenRefresh();

            setTimeout(() => {
              // Retry all pending requests with the new token
              for (const callback of pendingRequests) callback(authStore.accessToken!);
              pendingRequests = []; // Clear the queue after retrying

              // This replay's answer is thrown away and the caller has already been handed the 401,
              // so a request that is unsafe to repeat is left to its caller, who sees the refusal
              // and can read the record again; see `RequestConfig.skipNetworkRetry`. A download is
              // left too: its file could never reach the caller, so the server would rebuild the
              // export for nothing before the user's own second click.
              if (
                !error.config ||
                (error.config as RequestConfig).skipNetworkRetry ||
                isFileDownload(error.config)
              ) {
                return;
              }

              // Nothing awaits this replay and its caller already has the 401, so a failure is dropped
              // rather than left unhandled carrying the refreshed token, as `retryHandler` does.
              axios
                .request(withBearerToken(error.config, authStore.accessToken!))
                .catch(() => undefined);
            }, 1000);
          } catch {
            await router.push({ name: 'login' });
            return Promise.reject(new Error('Token refresh failed'));
          } finally {
            isRefreshInProgress = false; // Unlock the refresh process
          }

          // Context: a download's caller gets this 401 and saves no file, and the download stores
          // only log it, so the toast is the user's one sign to click again on the fresh token. It
          // sits outside the `try` so a failing toast never reads as a failed refresh.
          if (!skipsErrorFeedback && isFileDownload(error.config)) surfaceTryAgainToast();
        } else {
          await router.push({ name: 'login' });
          throw new Error('Unauthorized after token refresh');
        }
      }
    } else if (skipsErrorFeedback) {
      // Every other answer is the caller's alone; see `RequestConfig.skipErrorFeedback`.
    } else if (error.response.status === 409) {
      const method = error.config?.method?.toUpperCase() ?? 'UNKNOWN';
      const url = error.config?.url ?? 'unknown URL';
      // The org-user role endpoints use 409 for "role already assigned", which
      // is a domain error, not an optimistic-concurrency version conflict.
      if (/\/organisations\/[^/]+\/users\/[^/]+\/roles(\/|$)/.test(url)) {
        if (!isSilent) processCustomError(error as AxiosError<ErrorResponse>);
      } else {
        // The request config carries the bearer token and the request body, so only identifiers
        // that find the conflict go to Sentry.
        Sentry.captureMessage('409 Conflict', {
          level: 'info',
          tags: { method, url },
          ...(config?.requestId !== undefined ? { extra: { requestId: config.requestId } } : {}),
        });
        // A caller that tells its own 409s apart keeps the record above but not the reload dialog.
        // One that renders the 409 in its body still gets the server's message as a toast, unless
        // it is silent; see `RequestConfig.handlesConflict`, `handlesErrors` and `silentErrors`.
        if (isCallerHandled) {
          if (!isSilent) processCustomError(error as AxiosError<ErrorResponse>);
        } else if (!(error.config as RequestConfig | undefined)?.handlesConflict) {
          useVersionConflictStore().show();
        }
      }
    } else if (error.response.status === 403 && authStore.isLoggedIn) {
      // PE-482: stale-permission recovery — rehydrate abilities once, retry the
      // request once, then surface a graceful message. Returns the retried
      // response so the caller's await receives it (silent recovery on success).
      return await handleStalePermission403(error, isSilent);
    } else if (!isCallerHandled && error.response.status === 404 && authStore.isLoggedIn) {
      // A 404 while logged in can mean the user lost access to the org entirely.
      await orgStore.getCurrentUserOrgs();
      if (!authStore.loggedInUser?.organisations.length) {
        await router.push({ name: 'no-user-orgs' });
      }
    } else if (!isSilent && error.response?.status >= 400 && error.response?.status !== 429) {
      // A 404 the caller handles lands here rather than above: it is a refusal of the action, not
      // a sign the user has lost the organisation, so it takes the generic toast.
      processCustomError(error as AxiosError<ErrorResponse>);
    }
  }

  throw error;
};

/**
 * Every rejection leaves here without its bearer token, including one from a queued replay through
 * the bare `axios` instance, which has no interceptors of its own.
 */
export const onRejected = (error: AxiosError): Promise<AxiosResponse> =>
  handleRejection(error).catch((error_: unknown) => {
    throw withoutCredentials(error_);
  });
